import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, track } from "../lib/api";
import { addProduct as ecwidAdd, ecwidConfigured, loadEcwid } from "../lib/ecwid";

const CartContext = createContext(null);
const STORAGE_KEY = "ccdc_cart_v1";

function optionsKey(options) {
  return JSON.stringify(options || {});
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verificationRef, setVerificationRef] = useState(
    () => localStorage.getItem("ccdc_age_verify") || ""
  );
  const [verifyCallback, setVerifyCallback] = useState(null);
  const [verifyCancel, setVerifyCancel] = useState(null);

  // Gate checkout behind 21+ ID verification (custom modal → object storage
  // with MongoDB fallback). Saved state is re-validated against the server
  // every time — browsers lie about "verified" state.
  const verifyGate = useCallback(async (cb) => {
    const email = localStorage.getItem("ccdc_verify_email");
    if (email) {
      try {
        const s = await api.verificationStatus(email);
        if (s.verified) return cb();
      } catch (_) {
        return cb(); // network down — don't block; the sweep flags missing IDs
      }
      localStorage.removeItem("ccdc_age_verify");
      localStorage.removeItem("ccdc_verify_email");
      setVerificationRef("");
    }
    setVerifyCallback(() => cb);
    setVerifyCancel(null);
    setVerifyOpen(true);
  }, []);

  // Force the modal (checkout page gate) — onCancel runs if it's dismissed.
  const openVerification = useCallback((cb, onCancel) => {
    setVerifyCallback(() => cb);
    setVerifyCancel(() => onCancel || null);
    setVerifyOpen(true);
  }, []);

  const cancelVerification = useCallback(() => {
    setVerifyOpen(false);
    setVerifyCancel((cb) => {
      if (cb) cb();
      return null;
    });
  }, []);

  const completeVerification = useCallback((ref) => {
    localStorage.setItem("ccdc_age_verify", ref);
    setVerificationRef(ref);
    setVerifyOpen(false);
    setVerifyCancel(null);
    setVerifyCallback((cb) => {
      if (cb) cb();
      return null;
    });
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  // Warm the Ecwid JS API in the background so add-to-cart mirrors live.
  useEffect(() => {
    if (ecwidConfigured) loadEcwid().catch(() => {});
  }, []);

  const addItem = useCallback((product, qty = 1, options = {}, optionsDisplay = []) => {
    const key = `${product.id}:${optionsKey(options)}`;
    setItems((prev) => {
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) => (i.key === key ? { ...i, qty: i.qty + qty } : i));
      }
      return [
        ...prev,
        {
          key,
          id: product.id,
          slug: product.slug,
          name: product.name,
          price: product.price,
          image: product.image,
          options,
          optionsDisplay,
          qty,
        },
      ];
    });
    track("add_to_cart", { productId: product.id, name: product.name, qty, price: product.price });
    if (ecwidConfigured) {
      loadEcwid()
        .then((ok) => ok && ecwidAdd(product.id, qty, options))
        .catch(() => {
          // Required upload options etc. are completed inside the Ecwid checkout widget.
        });
    }
    toast.success(`${product.name} added to cart`);
  }, []);

  const removeItem = useCallback((key) => {
    setItems((prev) => {
      const target = prev.find((i) => i.key === key);
      if (target) track("remove_from_cart", { productId: target.id, name: target.name });
      return prev.filter((i) => i.key !== key);
    });
  }, []);

  const setQty = useCallback(
    (key, qty) => {
      if (qty <= 0) return removeItem(key);
      setItems((prev) => prev.map((i) => (i.key === key ? { ...i, qty } : i)));
    },
    [removeItem]
  );

  const clearCart = useCallback(() => setItems([]), []);

  const value = useMemo(() => {
    const count = items.reduce((n, i) => n + i.qty, 0);
    const subtotal = items.reduce((n, i) => n + i.qty * (i.price || 0), 0);
    return {
      items,
      count,
      subtotal,
      addItem,
      removeItem,
      setQty,
      clearCart,
      drawerOpen,
      setDrawerOpen,
      verifyOpen,
      setVerifyOpen,
      verifyGate,
      openVerification,
      cancelVerification,
      completeVerification,
      verificationRef,
    };
  }, [items, addItem, removeItem, setQty, clearCart, drawerOpen, verifyOpen,
      verifyGate, openVerification, cancelVerification, completeVerification, verificationRef]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  return useContext(CartContext);
}
